import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { authFetch } from "../services/authService";
import { 
  LayoutDashboard, 
  FileText, 
  Package, 
  Users,
  LogOut,
  Bell,
  ArrowLeft,
  Award,
  Truck,
  HelpCircle,
  X
} from "lucide-react";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import SupportRequestDialog from "../components/SupportRequestDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RicambistaLayout({ user, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [menuBadges, setMenuBadges] = useState({
    richieste: 0,
    ordini: 0,
    officine: 0,
    promozioni: 0
  });

  // Badges = items NEW since last visit (cleared on entering the section)
  const badgeStorageKey = `psvc_badge_seen_${user?.user_id || "anon"}`;
  const [badgeSeen, setBadgeSeen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(badgeStorageKey) || "{}"); }
    catch { return {}; }
  });
  const persistBadgeSeen = (next) => {
    setBadgeSeen(next);
    try { localStorage.setItem(badgeStorageKey, JSON.stringify(next)); } catch (e) {}
  };

  // Reload acknowledged baseline once user is available (fixes badge reappearing after reload)
  useEffect(() => {
    try {
      setBadgeSeen(JSON.parse(localStorage.getItem(badgeStorageKey) || "{}"));
    } catch { setBadgeSeen({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);
  const getDisplayBadge = (badgeKey) => {
    if (!badgeKey) return 0;
    const raw = menuBadges[badgeKey] || 0;
    return Math.max(0, raw - (badgeSeen[badgeKey] || 0));
  };

  // Navigation items with badge keys
  const navItems = [
    { path: "/ricambista", icon: LayoutDashboard, label: "Dashboard", end: true, badgeKey: null },
    { path: "/ricambista/richieste", icon: FileText, label: "Richieste Preventivo", badgeKey: "richieste" },
    { path: "/ricambista/ordini-gestione", icon: Truck, label: "Ordini da Gestire", badgeKey: "ordini" },
    { path: "/ricambista/officine", icon: Users, label: "Archivio Officine", badgeKey: "officine" },
    { path: "/ricambista/promozioni", icon: Award, label: "Campagne Punti", badgeKey: "promozioni" },
  ];

  // Clear a section badge when entering it
  useEffect(() => {
    const item = navItems.find((i) => i.path === location.pathname && i.badgeKey);
    if (item) {
      const raw = menuBadges[item.badgeKey] || 0;
      if ((badgeSeen[item.badgeKey] || 0) !== raw) {
        persistBadgeSeen({ ...badgeSeen, [item.badgeKey]: raw });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, menuBadges]);

  useEffect(() => {
    fetchNotifications();
    fetchMenuBadges();
    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchMenuBadges();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        authFetch(`${API}/notifications?limit=50`, { }),
        authFetch(`${API}/notifications/unread-count`, { })
      ]);
      if (notifRes.ok) {
        const data = await notifRes.json();
        // Bell shows only UNREAD notifications; reading removes them
        setNotifications((data || []).filter(n => !n.read).slice(0, 15));
      }
      if (countRes.ok) {
        const countData = await countRes.json();
        setUnreadCount(countData.count || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const fetchMenuBadges = async () => {
    try {
      // Fetch counts for each menu item
      const [statsRes, richiesteRes, ordiniRes] = await Promise.all([
        authFetch(`${API}/ricambista/stats`, { }),
        authFetch(`${API}/ricambista/requests/pending-count`, { }),
        authFetch(`${API}/ricambista/orders/pending-count`, { })
      ]);
      
      let badges = { richieste: 0, ordini: 0, officine: 0, promozioni: 0 };
      
      if (statsRes.ok) {
        const stats = await statsRes.json();
        badges.richieste = stats.pending_requests || 0;
        badges.ordini = stats.orders_today || 0;
        badges.officine = stats.inactive_officine || 0;
      }
      
      if (richiesteRes.ok) {
        const data = await richiesteRes.json();
        badges.richieste = data.count || badges.richieste;
      }
      
      if (ordiniRes.ok) {
        const data = await ordiniRes.json();
        badges.ordini = data.count || badges.ordini;
      }
      
      setMenuBadges(badges);
    } catch (error) {
      console.error("Error fetching menu badges:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, {
        method: "POST"
      });
      navigate("/");
      toast.success("Logout effettuato");
    } catch (error) {
      toast.error("Errore durante il logout");
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await authFetch(`${API}/notifications/${notificationId}/read`, {
        method: "PATCH"
      });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const wasUnread = notifications.find(n => n.notification_id === notificationId && !n.read);
      await authFetch(`${API}/notifications/${notificationId}`, { method: "DELETE" });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await authFetch(`${API}/notifications/read-all`, {
        method: "PATCH"
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const deleteAllNotifications = async () => {
    try {
      await authFetch(`${API}/notifications`, { method: "DELETE" });
      setNotifications([]);
      setUnreadCount(0);
      toast.success("Notifiche eliminate");
    } catch (error) {
      toast.error("Errore nell'eliminazione delle notifiche");
    }
  };

  // Map notification type -> destination route (ricambista side)
  const getNotificationLink = (notif) => {
    const map = {
      new_order: "/ricambista/ordini-gestione",
      order_received: "/ricambista/ordini-gestione",
      quote_received: "/ricambista/richieste",
      quote_response: "/ricambista/richieste",
      request_status_update: "/ricambista/richieste",
      parts_response: "/ricambista/richieste",
      earned: "/points",
      spent: "/points",
    };
    return map[notif.type] || null;
  };

  const handleNotificationClick = (notif) => {
    if (!notif.read) markAsRead(notif.notification_id);
    const link = getNotificationLink(notif);
    if (link) navigate(link);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-56 bg-zinc-950 border-r border-zinc-800 flex flex-col fixed h-full">
        {/* Logo */}
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-white">RICAMBISTA</span>
              <p className="text-[10px] text-zinc-500">Portale Ricambi</p>
            </div>
          </div>
        </div>

        {/* Back to App Link */}
        <NavLink
          to="/dashboard"
          className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800/50 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna all'App
        </NavLink>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map((item) => {
            const badgeCount = getDisplayBadge(item.badgeKey);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    isActive
                      ? "bg-blue-500/20 text-blue-400 font-medium"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                  }`
                }
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm">{item.label}</span>
                </div>
                {badgeCount > 0 && (
                  <span className="min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-medium">
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User Menu */}
        <div className="p-4 border-t border-zinc-800">
          {/* Support Button */}
          <div className="mb-3">
            <SupportRequestDialog user={user} triggerClassName="w-full justify-center" />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full hover:bg-zinc-800/50 rounded-lg p-2 transition-colors">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-blue-500 text-white text-xs">
                    {user?.name?.charAt(0) || "R"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-blue-400">Ricambista</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                Profilo
              </DropdownMenuItem>
              <DropdownMenuItem 
                data-testid="ricambista-download-manual-btn"
                onClick={async () => {
                  try {
                    const response = await authFetch(`${API}/manual/download`);
                    if (response.ok) {
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'PService_Pro_Manuale_Utente.pdf';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    }
                  } catch (err) { console.error(err); }
                }}
              >
                Scarica Manuale
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-400">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-56">
        {/* Top Bar with Notifications */}
        <header className="h-14 border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm flex items-center justify-end px-6 sticky top-0 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="relative p-2 hover:bg-zinc-800 rounded-lg transition-colors">
                <Bell className="w-5 h-5 text-zinc-400" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="p-2 border-b border-zinc-800 flex items-center justify-between">
                <h4 className="font-semibold text-white">Notifiche</h4>
                <div className="flex items-center gap-2">
                  {notifications.length > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      data-testid="mark-all-read-btn"
                      className="text-xs text-primary hover:underline"
                    >
                      Segna tutto letto
                    </button>
                  )}
                  <button
                    onClick={deleteAllNotifications}
                    data-testid="delete-all-notifications-btn"
                    className="text-xs text-red-400 hover:underline"
                  >
                    Cancella tutte
                  </button>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-zinc-500 text-sm">
                    Nessuna notifica
                  </div>
                ) : (
                  notifications.slice(0, 15).map((notif) => (
                    <div
                      key={notif.notification_id}
                      data-testid={`notification-${notif.notification_id}`}
                      className={`flex items-start gap-2 p-3 border-b border-zinc-800 ${!notif.read ? 'bg-blue-500/10' : ''}`}
                    >
                      <div
                        className="flex flex-col items-start flex-1 min-w-0 cursor-pointer"
                        onClick={() => handleNotificationClick(notif)}
                      >
                        <span className="text-sm text-white">{notif.title}</span>
                        <span className="text-xs text-zinc-400 line-clamp-2">{notif.message}</span>
                        <span className="text-xs text-zinc-500 mt-1">{notif.time}</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteNotification(notif.notification_id); }}
                        data-testid={`delete-notification-${notif.notification_id}`}
                        className="p-1 rounded hover:bg-zinc-700 text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0"
                        title="Elimina notifica"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
